import {
  ArrowRight,
  ArrowUpRight,
  CalendarDays,
  Church,
  Clock3,
  Facebook,
  HandHeart,
  Instagram,
  MapPin,
  Music2,
  Sparkles,
  UsersRound,
} from 'lucide-react';
import {
  heroPoster,
  heroVideo,
  imgAdoracion,
  imgAlcanceCalles,
  imgAlcanceCartel,
  imgAlcanceOracion,
  imgCongregacion,
  imgCongregacionWide,
  imgPastores,
} from '../assets';
import { Layout } from '../components/Layout';
import { EventsSlider } from '../components/EventsSlider';
import { useEvents, useNextService, useReveal } from '../hooks';
import { churchAddress, facebookUrl, instagramUrl, mapEmbedUrl, mapUrl, services } from '../site';

const ministries = [
  {
    icon: Music2,
    image: imgAdoracion,
    alt: 'Ministerio de adoración de Tercer Cielo durante un servicio',
    title: 'Adoración',
    text: 'Un equipo que levanta la alabanza y prepara el ambiente para encontrarnos con Dios.',
    tag: 'Cada servicio',
  },
  {
    icon: HandHeart,
    image: imgAlcanceCalles,
    imagePosition: 'center 16%',
    alt: 'Miembros de Tercer Cielo compartiendo el mensaje en la calle',
    title: 'Alcance',
    text: 'Salimos a las calles de Juárez a orar, abrazar y compartir la esperanza de Jesús.',
    tag: 'En la ciudad',
  },
];

const galleryItems = [
  {
    image: imgCongregacionWide,
    alt: 'Congregación reunida bajo el techo iluminado de la iglesia',
    caption: 'Una casa que crece',
    className: 'gallery-feature',
  },
  {
    image: imgCongregacion,
    alt: 'Comunidad de Tercer Cielo reunida en el auditorio',
    caption: 'Fe en familia',
    className: 'gallery-tall',
  },
  {
    image: imgAlcanceOracion,
    alt: 'La congregación compartiendo y orando juntos al aire libre',
    caption: 'Compartimos a Jesús',
    className: 'gallery-small',
  },
  {
    image: imgAlcanceCartel,
    alt: 'Jóvenes con un cartel de esperanza: “En la tormenta, Él calma el mar de tu corazón”',
    caption: 'Esperanza en la calle',
    className: 'gallery-small',
  },
];

function NextServiceChip() {
  const next = useNextService();

  return (
    <a className="hero-next-service" href="/visita/" aria-label="Ver detalles de la visita">
      <span className="pulse-dot" aria-hidden="true" />
      <span className="next-service-text">
        <small>Próximo encuentro · {next.when}</small>
        <strong>
          {next.service.day} {next.service.time} <em>· en {next.countdown}</em>
        </strong>
      </span>
      <ArrowUpRight size={18} aria-hidden="true" />
    </a>
  );
}

function Hero() {
  return (
    <section className="hero" aria-labelledby="hero-title">
      <div
        className="hero-media"
        style={{ backgroundImage: `url(${heroPoster})` }}
        aria-hidden="true"
      >
        <video
          src={heroVideo}
          poster={heroPoster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        />
        <div className="hero-overlay" />
      </div>

      <div className="page-shell hero-content">
        <p className="eyebrow eyebrow-light reveal is-visible">
          <span aria-hidden="true" /> Iglesia Cristiana · Ciudad Juárez
        </p>
        <h1 id="hero-title" className="reveal is-visible">
          Un lugar para encontrarte <em>con Dios</em> y con propósito.
        </h1>
        <p className="hero-lead reveal is-visible">
          Somos una comunidad que crece en fe, vive en familia y comparte a Jesús.
          Aquí hay un lugar para ti.
        </p>
        <div className="hero-actions reveal is-visible">
          <a className="button button-gold" href="/visita/">
            Planifica tu visita <ArrowRight size={18} aria-hidden="true" />
          </a>
          <a
            className="button button-ghost"
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Facebook size={18} aria-hidden="true" /> Míranos en Facebook
          </a>
        </div>
        <div className="hero-extra reveal is-visible">
          <NextServiceChip />
        </div>
      </div>

      <div className="hero-scroll-cue" aria-hidden="true">
        <span>Desliza</span>
        <i />
      </div>
    </section>
  );
}

function VerseStrip() {
  const phrase = 'Por un avivamiento del cielo en los corazones';
  const repetitions = Array.from({ length: 6 }, (_, index) => (
    <span key={index} aria-hidden={index > 0}>
      {phrase} <Sparkles size={16} aria-hidden="true" />
    </span>
  ));

  return (
    <div className="verse-strip" aria-label={phrase}>
      <div className="verse-strip-track">{repetitions}</div>
    </div>
  );
}

function ServicesSection() {
  const next = useNextService();

  return (
    <section className="services-section section-space" aria-labelledby="services-title">
      <div className="page-shell">
        <div className="section-heading services-heading reveal">
          <div>
            <p className="section-kicker">
              <span /> Nos reunimos cada semana
            </p>
            <h2 id="services-title">Hay un encuentro para ti.</h2>
          </div>
          <a className="text-link" href={mapUrl} target="_blank" rel="noopener noreferrer">
            Cómo llegar <ArrowUpRight size={17} aria-hidden="true" />
          </a>
        </div>
        <div className="services-grid">
          {services.map((service, index) => {
            const isNext = service === next.service;
            return (
              <article
                className={`service-card reveal ${isNext ? 'is-next' : ''}`}
                key={`${service.day}-${service.time}`}
              >
                {isNext && (
                  <span className="service-next-badge">
                    <span className="pulse-dot" aria-hidden="true" /> Siguiente · {next.countdown}
                  </span>
                )}
                <div className="service-number">0{index + 1}</div>
                <div className="service-day">
                  <span className="status-dot" aria-hidden="true" />
                  {service.day}
                </div>
                <time>{service.time}</time>
                <p>{service.title}</p>
                <small>{service.note}</small>
                <span className="service-icon" aria-hidden="true">
                  <Clock3 size={19} />
                </span>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function EventsSection() {
  const { events, status } = useEvents();

  return (
    <section className="events-home-section section-space" aria-labelledby="events-home-title">
      <div className="page-shell">
        <div className="section-heading reveal">
          <div>
            <p className="section-kicker">
              <span /> Agenda de la casa
            </p>
            <h2 id="events-home-title">Próximos eventos.</h2>
          </div>
          <a className="text-link" href="/eventos/">
            Ver todos <ArrowUpRight size={17} aria-hidden="true" />
          </a>
        </div>

        {status === 'loading' && (
          <div className="events-empty reveal">
            <CalendarDays size={28} aria-hidden="true" />
            <p>Cargando la agenda…</p>
          </div>
        )}
        {status === 'error' && (
          <div className="events-empty reveal">
            <CalendarDays size={28} aria-hidden="true" />
            <p>No pudimos cargar los eventos en este momento.</p>
          </div>
        )}
        {status === 'ready' && events.length === 0 && (
          <div className="events-empty reveal">
            <CalendarDays size={28} aria-hidden="true" />
            <p>Pronto anunciaremos nuevos eventos. Te esperamos en nuestros servicios semanales.</p>
          </div>
        )}
        {status === 'ready' && events.length > 0 && <EventsSlider events={events} />}
      </div>
    </section>
  );
}

function MinistriesSection() {
  return (
    <section className="ministries-section section-space" aria-labelledby="ministries-title">
      <div className="page-shell">
        <div className="section-heading reveal">
          <div>
            <p className="section-kicker">
              <span /> La casa late fuerte
            </p>
            <h2 id="ministries-title">Ministerios con vida.</h2>
          </div>
          <p>
            Cada semana la iglesia se enciende: adoración, ministración y una familia
            que sale a compartir lo que ha recibido.
          </p>
        </div>
        <div className="ministries-grid">
          {ministries.map(({ icon: Icon, image, imagePosition, alt, title, text, tag }) => (
            <article className="ministry-card reveal" key={title}>
              <div className="ministry-image">
                <img
                  src={image}
                  alt={alt}
                  loading="lazy"
                  style={imagePosition ? { objectPosition: imagePosition } : undefined}
                />
                <span className="ministry-tag">{tag}</span>
              </div>
              <div className="ministry-body">
                <span className="ministry-icon">
                  <Icon size={22} aria-hidden="true" />
                </span>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function PastorsSection() {
  return (
    <section className="pastors-section section-space" aria-labelledby="pastors-title">
      <div className="page-shell pastors-card reveal">
        <div className="pastors-image-wrap">
          <img
            src={imgPastores}
            alt="Los pastores Diana y Fernando Rentería orando junto a la congregación"
            loading="lazy"
          />
          <div className="pastors-image-tag">
            <Church size={18} aria-hidden="true" /> Sirviendo a Ciudad Juárez
          </div>
        </div>
        <div className="pastors-copy">
          <p className="section-kicker section-kicker-light">
            <span /> Nuestros pastores
          </p>
          <h2 id="pastors-title">Liderando con fe, servicio y cercanía.</h2>
          <p className="pastors-names">
            Diana <span>&amp;</span> Fernando Rentería
          </p>
          <p className="pastors-message">
            Nuestro deseo es que cada persona encuentre un espacio para acercarse a Dios,
            crecer en su Palabra y construir relaciones con propósito.
          </p>
          <div className="pastors-quote">
            <Sparkles size={20} aria-hidden="true" />
            <p>“Por un avivamiento del cielo en los corazones.”</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function GallerySection() {
  return (
    <section className="gallery-section section-space" aria-labelledby="gallery-title">
      <div className="page-shell">
        <div className="section-heading reveal">
          <div>
            <p className="section-kicker">
              <span /> Nuestra comunidad
            </p>
            <h2 id="gallery-title">La fe también se vive acompañados.</h2>
          </div>
          <p>
            Oramos, servimos y compartimos el mensaje de Jesús como una familia que
            hace espacio para cada persona.
          </p>
        </div>
        <div className="gallery-grid">
          {galleryItems.map((item, index) => (
            <figure className={`gallery-item reveal ${item.className}`} key={item.caption}>
              <img src={item.image} alt={item.alt} loading="lazy" />
              <figcaption>
                <span>0{index + 1}</span> {item.caption}
              </figcaption>
            </figure>
          ))}
          <div className="gallery-statement reveal">
            <span className="statement-icon">
              <UsersRound size={24} aria-hidden="true" />
            </span>
            <p>Una comunidad que escucha, acompaña y sirve.</p>
            <a href={instagramUrl} target="_blank" rel="noopener noreferrer">
              Ver más en Instagram <ArrowUpRight size={17} aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function SocialCtaSection() {
  return (
    <section className="social-section" aria-labelledby="social-title">
      <div className="page-shell social-layout">
        <div className="social-copy reveal">
          <p className="section-kicker section-kicker-light">
            <span /> Conéctate con nosotros
          </p>
          <h2 id="social-title">La iglesia, también en tu pantalla.</h2>
          <p>
            Síguenos en Facebook para ver las transmisiones de los servicios, los
            anuncios de la semana y lo que Dios está haciendo en la casa.
          </p>
        </div>
        <div className="social-cards">
          <a
            className="social-card social-card-facebook reveal"
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="social-card-icon">
              <Facebook size={26} aria-hidden="true" />
            </span>
            <div>
              <strong>Facebook</strong>
              <p>Transmisiones en vivo y mensajes recientes.</p>
            </div>
            <ArrowUpRight size={20} aria-hidden="true" />
          </a>
          <a
            className="social-card social-card-instagram reveal"
            href={instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="social-card-icon">
              <Instagram size={26} aria-hidden="true" />
            </span>
            <div>
              <strong>Instagram</strong>
              <p>El día a día de la comunidad, en fotos.</p>
            </div>
            <ArrowUpRight size={20} aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}

function VisitSection() {
  return (
    <section className="visit-section section-space" aria-labelledby="visit-title">
      <div className="visit-orb" aria-hidden="true" />
      <div className="page-shell visit-layout">
        <div className="visit-copy reveal">
          <p className="section-kicker section-kicker-light">
            <span /> Planea tu visita
          </p>
          <h2 id="visit-title">Nos encantará conocerte.</h2>
          <p>
            Guarda nuestros horarios, abre la ubicación y ven a compartir con nosotros.
            Aquí hay un lugar para ti.
          </p>
          <div className="visit-actions">
            <a className="button button-light" href="/visita/">
              Detalles de tu visita <ArrowRight size={18} aria-hidden="true" />
            </a>
            <a
              className="text-link text-link-light"
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Abrir en Google Maps <ArrowUpRight size={17} aria-hidden="true" />
            </a>
          </div>
        </div>
        <div className="visit-details reveal">
          <div className="visit-map">
            <iframe
              title="Mapa de Iglesia Cristiana Tercer Cielo"
              src={mapEmbedUrl}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <a
            className="visit-detail visit-detail-link"
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>
              <MapPin size={21} aria-hidden="true" />
            </span>
            <div>
              <small>Dirección</small>
              <strong>{churchAddress.street}</strong>
              <p>{churchAddress.locality}</p>
            </div>
            <ArrowUpRight className="visit-detail-arrow" size={18} aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}

export function HomePage() {
  const { status } = useEvents();
  useReveal(status);

  return (
    <Layout>
      <Hero />
      <VerseStrip />
      <ServicesSection />
      <EventsSection />
      <MinistriesSection />
      <PastorsSection />
      <GallerySection />
      <SocialCtaSection />
      <VisitSection />
    </Layout>
  );
}
